import React, { useEffect, useState } from "react";
import Modal from "./Modal";

const getVisibleKeys = (row) =>
  Object.keys(row).filter(
    (key) => key !== "id" && key !== "item_id" && key !== "is_internal",
  );

const formatHeader = (key) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const isNumericDisplay = (value) => {
  if (typeof value === "number") return true;
  if (typeof value !== "string") return false;

  const cleaned = value.replace(/[£,\s]/g, "");
  return cleaned !== "" && !Number.isNaN(Number(cleaned));
};

const MAIN_PROTEIN_IDS = [103, 104, 105, 106, 107, 108];

const AnalyticsModals = ({
  modalData,
  modalTitle,
  detailsModal,
  onCloseMain,
  onCloseDetails,
}) => {
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  useEffect(() => {
    setExpandedOrderId(null);
  }, [detailsModal.open, detailsModal.title, detailsModal.type]);

  const toggleOrder = (orderId) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  };

  const getOrderTypeClasses = (orderType) => {
    const type = String(orderType || "")
      .trim()
      .toLowerCase();

    if (type === "delivery") return "bg-blue-100 text-blue-700";
    if (type === "pickup") return "bg-amber-100 text-amber-700";
    if (type === "eat-in" || type === "dine-in") {
      return "bg-emerald-100 text-emerald-700";
    }

    return "bg-slate-100 text-slate-700";
  };

  const renderOrderItems = (items = []) => {
    if (!Array.isArray(items) || !items.length) {
      return (
        <p className="text-sm italic text-slate-500">No items available.</p>
      );
    }

    return (
      <div className="space-y-3">
        {items.map((item) => {
          const mainProteins =
            item.proteins?.filter((p) =>
              MAIN_PROTEIN_IDS.includes(Number(p.id)),
            ) || [];

          const extraProteins =
            item.proteins?.filter(
              (p) => !MAIN_PROTEIN_IDS.includes(Number(p.id)),
            ) || [];

          const isRemoved =
            Number(item?.is_removed) === 1 || item?.is_removed === true;

          return (
            <div
              key={item.id}
              className={`relative rounded-lg border p-3 ${
                isRemoved
                  ? "border-slate-300 bg-slate-100/90 opacity-60"
                  : "border-slate-200"
              }`}
            >
              {isRemoved && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-600/90 text-white shadow-lg">
                    <i className="fa-solid fa-ban text-lg"></i>
                  </div>
                </div>
              )}

              <div className="flex justify-between gap-4">
                <div>
                  <p
                    className={`font-medium ${
                      isRemoved
                        ? "text-slate-400 line-through"
                        : "text-slate-900"
                    }`}
                  >
                    {item.name} x {item.quantity}
                  </p>

                  {item.required_option_name && (
                    <p
                      className={`mt-1 ${
                        isRemoved
                          ? "text-slate-400 line-through"
                          : "text-slate-600"
                      }`}
                    >
                      {item.required_option_name}
                      {parseFloat(item.required_option_price || 0) > 0
                        ? ` (£${parseFloat(item.required_option_price).toFixed(2)})`
                        : ""}
                    </p>
                  )}

                  {(item.selectedOptionalOptions || []).length > 0 && (
                    <div className="mt-2">
                      <p
                        className={`font-medium ${
                          isRemoved ? "text-slate-400" : "text-slate-700"
                        }`}
                      >
                        Optional:
                      </p>
                      <ul
                        className={`ml-5 list-disc ${
                          isRemoved ? "text-slate-400" : "text-slate-600"
                        }`}
                      >
                        {item.selectedOptionalOptions.map((opt, idx) => (
                          <li
                            key={idx}
                            className={isRemoved ? "line-through" : ""}
                          >
                            {opt.name} x {opt.quantity} (£
                            {parseFloat(opt.price || 0).toFixed(2)})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {(item.swallowOptions || []).length > 0 && (
                    <div className="mt-2">
                      <p
                        className={`font-medium ${
                          isRemoved ? "text-slate-400" : "text-slate-700"
                        }`}
                      >
                        Swallow:
                      </p>
                      <ul
                        className={`ml-5 list-disc ${
                          isRemoved ? "text-slate-400" : "text-slate-600"
                        }`}
                      >
                        {item.swallowOptions.map((opt, idx) => (
                          <li
                            key={idx}
                            className={isRemoved ? "line-through" : ""}
                          >
                            {opt.name} x {opt.quantity} (£
                            {parseFloat(opt.price || 0).toFixed(2)})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {mainProteins.length > 0 && (
                    <div className="mt-2">
                      <p
                        className={`font-medium ${
                          isRemoved ? "text-slate-400" : "text-slate-700"
                        }`}
                      >
                        Protein(s):
                      </p>
                      <ul
                        className={`ml-5 list-disc ${
                          isRemoved ? "text-slate-400" : "text-slate-600"
                        }`}
                      >
                        {mainProteins.map((opt, idx) => (
                          <li
                            key={idx}
                            className={isRemoved ? "line-through" : ""}
                          >
                            {opt.name} x {opt.quantity} (£
                            {parseFloat(opt.price || 0).toFixed(2)})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {extraProteins.length > 0 && (
                    <div className="mt-2">
                      <p
                        className={`font-medium ${
                          isRemoved ? "text-slate-400" : "text-slate-700"
                        }`}
                      >
                        Extra:
                      </p>
                      <ul
                        className={`ml-5 list-disc ${
                          isRemoved ? "text-slate-400" : "text-slate-600"
                        }`}
                      >
                        {extraProteins.map((opt, idx) => (
                          <li
                            key={idx}
                            className={isRemoved ? "line-through" : ""}
                          >
                            {opt.name} x {opt.quantity} (£
                            {parseFloat(opt.price || 0).toFixed(2)})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {isRemoved && (
                    <p className="mt-2 text-xs font-semibold text-red-500">
                      This item was removed from the order.
                    </p>
                  )}
                </div>

                <div
                  className={`text-right font-semibold ${
                    isRemoved ? "text-slate-400 line-through" : "text-slate-900"
                  }`}
                >
                  £{parseFloat(item.total_price || 0).toFixed(2)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderOrdersByDayCards = () => {
    const dayTotal = detailsModal.rows.reduce(
      (sum, order) => sum + parseFloat(order.total_amount || order.total || 0),
      0,
    );

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
          <p className="text-sm text-slate-600">
            Total Orders:{" "}
            <span className="font-semibold">{detailsModal.rows.length}</span>
          </p>

          <p className="text-sm text-slate-600">
            Total:{" "}
            <span className="font-semibold text-slate-900">
              £{dayTotal.toFixed(2)}
            </span>
          </p>
        </div>

        <div className="max-h-[70vh] space-y-3 overflow-auto">
          {detailsModal.rows.map((order) => {
            const isDelivery =
              String(order.order_type || "")
                .trim()
                .toLowerCase() === "delivery";

            return (
              <div
                key={order.id}
                className="cursor-pointer rounded-lg border p-4 hover:bg-slate-50"
                onClick={() => toggleOrder(order.id)}
              >
                <div className="flex justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                      <span>{order.order_date || "—"}</span>
                      <span>{order.order_time || "—"}</span>
                      {order.order_number && (
                        <span className="font-medium">
                          #{order.order_number}
                        </span>
                      )}
                    </div>

                    <p className="font-semibold text-slate-900">
                      Order ID: L-{order.id}
                    </p>

                    <div className="flex flex-wrap items-center gap-4 text-sm">
                      <p className="text-slate-600">
                        {order.customer_name || "Walk-in / Unknown"}
                      </p>

                      {order.customer_phone && (
                        <p className="text-slate-500">{order.customer_phone}</p>
                      )}
                    </div>
                  </div>

                  <div className="text-right">
                    <p className="font-semibold text-slate-900">
                      £
                      {parseFloat(
                        order.total_amount || order.total || 0,
                      ).toFixed(2)}
                    </p>

                    <span
                      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${getOrderTypeClasses(order.order_type)}`}
                    >
                      {order.order_type || "—"}
                    </span>
                  </div>
                </div>

                {expandedOrderId === order.id && (
                  <div
                    className="mt-4 space-y-4 text-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isDelivery && (
                      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                        <p className="font-semibold text-slate-800">
                          Delivery Details
                        </p>

                        {order.delivery_address && (
                          <p className="mt-2 text-slate-700">
                            <strong>Address:</strong> {order.delivery_address}
                          </p>
                        )}

                        {order.postcode && (
                          <p className="text-slate-700">
                            <strong>Postcode:</strong> {order.postcode}
                          </p>
                        )}

                        {order.delivery_fee != null && (
                          <p className="text-slate-700">
                            <strong>Delivery Fee:</strong> £
                            {parseFloat(order.delivery_fee || 0).toFixed(2)}
                          </p>
                        )}

                        {parseFloat(order.service_charge || 0) > 0 && (
                          <p className="text-slate-700">
                            <strong>Service Charge:</strong> £
                            {parseFloat(order.service_charge || 0).toFixed(2)}
                          </p>
                        )}
                      </div>
                    )}

                    {renderOrderItems(order.items)}

                    {!isDelivery &&
                      parseFloat(order.service_charge || 0) > 0 && (
                        <p className="text-slate-700">
                          <strong>Service:</strong> £
                          {parseFloat(order.service_charge).toFixed(2)}
                        </p>
                      )}

                    {order.notes && (
                      <p className="rounded-lg bg-amber-50 p-3 text-slate-700">
                        <strong>Notes:</strong> {order.notes}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderDetailsModalContent = () => {
    if (detailsModal.loading) {
      return (
        <div className="flex justify-center py-10">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-300 border-t-slate-900" />
        </div>
      );
    }

    if (detailsModal.message) {
      return (
        <div className="py-8 text-center text-sm text-red-500">
          {detailsModal.message}
        </div>
      );
    }

    if (!detailsModal.rows.length) {
      return (
        <div className="py-8 text-center text-sm text-slate-400">
          No data available
        </div>
      );
    }

    if (detailsModal.type === "ordersByDay") {
      return renderOrdersByDayCards();
    }

    if (detailsModal.type === "itemBreakdown") {
      const totalQuantity = detailsModal.rows.reduce(
        (sum, row) => sum + Number(row.quantity_sold || 0),
        0,
      );

      const totalRevenue = detailsModal.rows.reduce(
        (sum, row) => sum + Number(row.total_revenue || 0),
        0,
      );

      return (
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full table-fixed text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-[0.12em] text-slate-500">
                <th className="w-[40%] py-3 pr-3 font-semibold break-words">
                  Date
                </th>
                <th className="w-[28%] py-3 px-2 text-center font-semibold break-words">
                  Quantity Sold
                </th>
                <th className="w-[32%] py-3 px-2 text-center font-semibold break-words">
                  Total Revenue (£)
                </th>
              </tr>
            </thead>

            <tbody>
              {detailsModal.rows.map((row, idx) => (
                <tr
                  key={idx}
                  className="border-b border-slate-50 last:border-b-0"
                >
                  <td className="py-3 pr-3 text-slate-700">{row.period}</td>
                  <td className="px-2 py-3 text-center tabular-nums text-slate-700">
                    {row.quantity_sold}
                  </td>
                  <td className="px-2 py-3 text-center tabular-nums font-semibold text-slate-700">
                    £{Number(row.total_revenue || 0).toFixed(2)}
                  </td>
                </tr>
              ))}

              <tr className="border-t-2 border-slate-200 bg-slate-50 font-semibold">
                <td className="py-3 pr-3 text-slate-900">Total</td>
                <td className="px-2 py-3 text-center tabular-nums text-slate-900">
                  {totalQuantity}
                </td>
                <td className="px-2 py-3 text-center tabular-nums text-slate-900">
                  £{totalRevenue.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    if (detailsModal.type === "loyaltyDetails") {
      const totalLoyalty = detailsModal.rows.reduce(
        (sum, row) => sum + parseFloat(row.amount || 0),
        0,
      );

      return (
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full table-fixed text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-[0.12em] text-slate-500">
                <th className="w-[20%] py-3 pr-3 font-semibold break-words">
                  Date
                </th>
                <th className="w-[40%] py-3 px-2 font-semibold break-words text-left">
                  Customer
                </th>
                <th className="w-[25%] py-3 px-2 font-semibold break-words text-left">
                  Phone
                </th>
                <th className="w-[15%] py-3 pl-2 text-right font-semibold break-words">
                  Amount (£)
                </th>
              </tr>
            </thead>

            <tbody>
              {detailsModal.rows.map((row, idx) => (
                <tr
                  key={idx}
                  className="border-b border-slate-50 last:border-b-0"
                >
                  <td className="py-3 pr-3 text-slate-700">{row.date}</td>
                  <td className="px-2 py-3 text-slate-700 font-medium">
                    {row.customer_name}
                  </td>
                  <td className="px-2 py-3 text-left text-slate-700 tabular-nums">
                    {row.phone}
                  </td>
                  <td className="py-3 pl-2 text-right tabular-nums font-semibold text-red-600">
                    £{parseFloat(row.amount || 0).toFixed(2)}
                  </td>
                </tr>
              ))}

              <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold">
                <td colSpan={3} className="py-3 pr-3 text-right text-slate-900">
                  Total Rewards Issued:
                </td>
                <td className="py-3 pl-2 text-right tabular-nums text-red-600">
                  £{totalLoyalty.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {modalData && (
        <Modal
          title={modalTitle}
          onClose={onCloseMain}
          showCancel={false}
          showSubmitButton={false}
          className="max-w-5xl"
        >
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full table-fixed text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-slate-100 text-left text-[11px] uppercase tracking-[0.12em] text-slate-500">
                  {modalData[0] &&
                    getVisibleKeys(modalData[0]).map((key, index) => (
                      <th
                        key={key}
                        className={`py-3 font-semibold break-words ${
                          index === 0 ? "pr-3" : "px-2 text-center"
                        }`}
                      >
                        {formatHeader(key)}
                      </th>
                    ))}
                </tr>
              </thead>

              <tbody>
                {modalData.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`border-b border-slate-50 last:border-b-0 ${
                      row.is_internal ? "bg-amber-50/80" : ""
                    }`}
                  >
                    {Object.entries(row)
                      .filter(
                        ([key]) =>
                          key !== "id" &&
                          key !== "item_id" &&
                          key !== "is_internal",
                      )
                      .map(([key, val], cellIndex) => {
                        let displayValue = val;

                        if (
                          key === "first_name" ||
                          key === "last_name" ||
                          key === "phone"
                        ) {
                          displayValue = val || "—";
                        }

                        if (
                          typeof val === "number" &&
                          (key.includes("revenue") || key === "total_value")
                        ) {
                          displayValue = val.toFixed(2);
                        }

                        const isNumeric = isNumericDisplay(
                          String(displayValue ?? ""),
                        );

                        return (
                          <td
                            key={key}
                            className={`py-3 ${
                              cellIndex === 0
                                ? row.is_internal
                                  ? "pr-3 text-amber-800"
                                  : "pr-3 text-slate-700"
                                : row.is_internal
                                  ? "px-2 text-center tabular-nums text-amber-800"
                                  : "px-2 text-center tabular-nums text-slate-700"
                            } ${isNumeric ? "font-medium" : ""}`}
                          >
                            {displayValue}
                            {key === "last_name" && row.is_internal ? (
                              <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                                Admin
                              </span>
                            ) : null}
                          </td>
                        );
                      })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}

      {detailsModal.open && (
        <Modal
          title={detailsModal.title}
          onClose={onCloseDetails}
          showCancel={false}
          showSubmitButton={false}
          className="max-w-5xl"
        >
          {renderDetailsModalContent()}
        </Modal>
      )}
    </>
  );
};

export default AnalyticsModals;
